import { useMemo, useState } from 'react'
import { AlertTriangle, Search, X } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { DataTable } from '@/components/DataTable'
import { Modal } from '@/components/Modal'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/useToast'
import { ApiError } from '@/lib/api'
import {
  useDbTable,
  useDbTables,
  useDeleteAllRows,
} from '@/features/platformAdmin/queries'

const errMsg = (err: unknown, fallback = 'Failed.') => {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

const cellValue = (v: unknown): string => {
  if (v == null) return '-'
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

export function DbToolsTab() {
  const { toast } = useToast()
  const tablesQ = useDbTables()
  const [selected, setSelected] = useState<string | null>(null)
  const [activeTable, setActiveTable] = useState<string | null>(null)
  const tableQ = useDbTable(activeTable)
  const deleteAll = useDeleteAllRows()

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmInput, setConfirmInput] = useState('')

  const tables = tablesQ.data?.tables ?? []

  const onLoad = () => {
    if (!selected) return
    setActiveTable(selected)
  }

  const requestDelete = () => {
    if (!activeTable) return
    setConfirmInput('')
    setConfirmOpen(true)
  }

  const submitDelete = async () => {
    if (!activeTable) return
    if (confirmInput !== activeTable) {
      toast({ variant: 'destructive', title: 'Confirmation did not match.' })
      return
    }
    try {
      const r = await deleteAll.mutateAsync(activeTable)
      toast({ title: `Removed ${r.deleted} row${r.deleted === 1 ? '' : 's'} from ${activeTable}.` })
      setConfirmOpen(false)
      // refresh by re-setting activeTable
      setActiveTable(activeTable)
    } catch (err) {
      toast({ variant: 'destructive', title: 'Failed', description: errMsg(err) })
    }
  }

  return (
    <div className="space-y-3">
      <Card className="border-warning/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-warning-foreground">
            <AlertTriangle className="size-4" />
            Dangerous tools
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            Read-only browse for any table; bulk delete is allowed for non-protected tables only.
            Every action is audited.
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 sm:max-w-sm">
              <label className="mb-1 block text-xs font-medium">Table</label>
              <Select value={selected ?? undefined} onValueChange={setSelected}>
                <SelectTrigger>
                  <SelectValue placeholder="- Select a table -" />
                </SelectTrigger>
                <SelectContent>
                  {tables.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={onLoad} disabled={!selected}>
              Load rows
            </Button>
            <Button variant="danger" onClick={requestDelete} disabled={!activeTable}>
              Delete ALL rows
            </Button>
          </div>
        </CardContent>
      </Card>

      {activeTable ? (
        tableQ.isLoading ? (
          <Card>
            <CardContent className="py-10">
              <LoadingSpinner />
            </CardContent>
          </Card>
        ) : tableQ.isError ? (
          <Alert variant="destructive">
            <AlertDescription>{errMsg(tableQ.error)}</AlertDescription>
          </Alert>
        ) : tableQ.data && tableQ.data.rows.length > 0 ? (
          <DbRowsTable data={tableQ.data} />
        ) : (
          <Alert>
            <AlertDescription>Empty table.</AlertDescription>
          </Alert>
        )
      ) : null}

      <Modal
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={`DELETE ALL ROWS from ${activeTable ?? ''}`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={submitDelete} disabled={deleteAll.isPending}>
              Delete
            </Button>
          </>
        }
      >
        <div className="space-y-2 text-sm">
          <p>
            This will delete <strong>
              {tableQ.data?.total != null
                ? `${tableQ.data.total.toLocaleString()} row${tableQ.data.total === 1 ? '' : 's'}`
                : 'every row'}
            </strong> from <code>{activeTable}</code>. This cannot be undone.
          </p>
          <p>
            Type <strong>{activeTable}</strong> below to confirm:
          </p>
          <Input
            value={confirmInput}
            onChange={(e) => setConfirmInput(e.currentTarget.value)}
            placeholder={activeTable ?? ''}
          />
        </div>
      </Modal>
    </div>
  )
}

function DbRowsTable({ data }: { data: import('@/features/platformAdmin/types').DbRowsResponse }) {
  const [filter, setFilter] = useState('')

  const columns = useMemo<ColumnDef<Record<string, unknown>>[]>(
    () =>
      data.columns.map((col) => ({
        id: col,
        accessorFn: (row) => cellValue(row[col]),
        header: col,
        cell: ({ getValue }) => {
          const v = String(getValue() ?? '-')
          return (
            <span
              className="block max-w-[280px] truncate text-muted-foreground"
              title={v}
            >
              {v.slice(0, 80)}
            </span>
          )
        },
      })),
    [data.columns],
  )

  const filteredRows = useMemo(() => {
    const q = filter.trim().toLowerCase()
    if (!q) return data.rows
    return data.rows.filter((row) =>
      data.columns.some((col) => cellValue(row[col]).toLowerCase().includes(q)),
    )
  }, [data.rows, data.columns, filter])

  return (
    <Card>
      <CardHeader className="space-y-3">
        <CardTitle className="text-base">
          {data.rows.length} of {data.total} rows from <code className="text-sm">{data.table}</code>
        </CardTitle>
        <div className="relative w-full sm:max-w-sm">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            placeholder="Filter loaded rows…"
            value={filter}
            onChange={(e) => setFilter(e.currentTarget.value)}
            className="pl-9 pr-9"
          />
          {filter ? (
            <button
              type="button"
              aria-label="Clear filter"
              onClick={() => setFilter('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          ) : null}
        </div>
        {filter.trim() ? (
          <span className="text-xs text-muted-foreground">
            {filteredRows.length} of {data.rows.length} loaded rows match. Filter is client-side over the loaded slice only - full table has {data.total}.
          </span>
        ) : null}
      </CardHeader>
      <CardContent className="p-0">
        <DataTable columns={columns} rows={filteredRows} sortable={false} />
      </CardContent>
    </Card>
  )
}
